import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import Layout from '../components/Layout';
import {
  Plus, Search, Eye, Pencil, Trash2, TrendingUp, X, Upload,
} from 'lucide-react';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/api\/?$/, '');
const API = `${BACKEND_URL}/api`;

const emptyForm = {
  amount: '',
  date: new Date().toISOString().split('T')[0],
  source: '',
  sub_category: '',
  notes: '',
};

export default function CreditList() {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const token = localStorage.getItem('token');
  const isSuperApprover = user.role === 'super_approver';

  const [credits, setCredits] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [month, setMonth] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null); // credit object being edited or null
  const [form, setForm] = useState(emptyForm);
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(new Set());

  const authHeader = { Authorization: `Bearer ${token}` };

  // Debounce search input
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(t);
  }, [search]);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: 20 };
      if (debouncedSearch) params.search = debouncedSearch;
      if (month) params.month = month;
      const { data } = await axios.get(`${API}/credits`, { params, headers: authHeader });
      setCredits(data.items || []);
      setTotal(data.total || 0);
      setTotalAmount(data.total_amount || 0);
      setTotalPages(data.total_pages || 1);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load credits');
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch, month]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const openAdd = () => {
    setEditing(null);
    setForm(emptyForm);
    setFiles([]);
    setShowDialog(true);
  };

  const openEdit = (credit) => {
    setEditing(credit);
    setForm({
      amount: credit.amount,
      date: credit.date,
      source: credit.source || '',
      sub_category: credit.sub_category || '',
      notes: credit.notes || '',
    });
    setFiles([]);
    setShowDialog(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.amount || Number(form.amount) <= 0) {
      toast.error('Amount must be greater than 0');
      return;
    }
    if (!form.source.trim()) {
      toast.error('Source is required');
      return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('amount', form.amount);
      fd.append('date', form.date);
      fd.append('source', form.source.trim());
      fd.append('submitted_by', user.name || user.email || 'Unknown');
      if (form.sub_category) fd.append('sub_category', form.sub_category);
      if (form.notes) fd.append('notes', form.notes);
      files.forEach((f) => fd.append('file', f));

      if (editing) {
        await axios.put(`${API}/credits/${editing.id}`, fd, {
          headers: { ...authHeader, 'Content-Type': 'multipart/form-data' },
        });
        toast.success('Credit updated');
      } else {
        await axios.post(`${API}/credits`, fd, {
          headers: { ...authHeader, 'Content-Type': 'multipart/form-data' },
        });
        toast.success('Credit added');
      }
      setShowDialog(false);
      setForm(emptyForm);
      setFiles([]);
      setEditing(null);
      fetchList();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save credit');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this credit? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/credits/${id}`, { headers: authHeader });
      toast.success('Credit deleted');
      fetchList();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    }
  };

  const bulkDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Delete ${selected.size} credit(s)? This cannot be undone.`)) return;
    try {
      await axios.post(`${API}/credits/bulk-delete`,
        { ids: Array.from(selected) },
        { headers: authHeader });
      toast.success(`Deleted ${selected.size} credit(s)`);
      setSelected(new Set());
      setSelectMode(false);
      fetchList();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    }
  };

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const formatDate = (d) => {
    if (!d) return '-';
    const [y, m, day] = d.split('-');
    return `${day}/${m}/${y}`;
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 py-6 space-y-5" data-testid="credits-page">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center shadow-lg">
              <TrendingUp className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-extrabold text-slate-900" style={{ fontFamily: "'Manrope', sans-serif" }}>Credits</h1>
              <p className="text-xs text-slate-500">Other income · refunds · rebates · scrap sales</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isSuperApprover && selectMode && (
              <>
                <Button variant="destructive" size="sm" onClick={bulkDelete} disabled={selected.size === 0} data-testid="credits-bulk-delete">
                  <Trash2 className="w-4 h-4 mr-1" /> Delete ({selected.size})
                </Button>
                <Button variant="outline" size="sm" onClick={() => { setSelectMode(false); setSelected(new Set()); }}>
                  <X className="w-4 h-4" />
                </Button>
              </>
            )}
            {isSuperApprover && !selectMode && (
              <Button variant="outline" size="sm" onClick={() => setSelectMode(true)} data-testid="credits-toggle-select">
                Select
              </Button>
            )}
            <Button onClick={openAdd} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="credits-add-btn">
              <Plus className="w-4 h-4 mr-1" /> Add Credit
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              placeholder="Search source, notes, id…"
              className="pl-9"
              data-testid="credits-search"
            />
          </div>
          <Input
            type="month"
            value={month}
            onChange={(e) => { setMonth(e.target.value); setPage(1); }}
            className="max-w-[180px]"
            data-testid="credits-month-filter"
          />
        </div>

        {/* Summary bar */}
        <div className="flex items-center justify-between px-5 py-3 bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-xl">
          <span className="text-xs uppercase tracking-wide font-semibold text-emerald-800">
            {total} {total === 1 ? 'credit' : 'credits'}{month ? ` · ${month}` : ''}
          </span>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wide text-emerald-700 font-semibold">Total Amount</div>
            <div className="text-lg font-extrabold text-emerald-900" data-testid="credits-total-amount">
              ₹{Number(totalAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
          </div>
        </div>

        {/* List */}
        {loading ? (
          <div className="text-center py-16 text-slate-400">Loading…</div>
        ) : credits.length === 0 ? (
          <div className="text-center py-16 text-slate-400">No credits yet. Click <b>Add Credit</b> to record your first entry.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {credits.map((c) => (
              <div
                key={c.id}
                className="bg-white border border-slate-200 rounded-2xl overflow-hidden hover:shadow-md transition-shadow"
                data-testid={`credit-card-${c.id}`}
              >
                <div className="p-3.5 flex items-center gap-3">
                  {selectMode && (
                    <input
                      type="checkbox"
                      checked={selected.has(c.id)}
                      onChange={() => toggleSelect(c.id)}
                      className="rounded flex-shrink-0"
                      data-testid={`credit-select-${c.id}`}
                    />
                  )}
                  <div className="flex-1 min-w-0 overflow-hidden">
                    <div className="flex items-center gap-2 min-w-0">
                      <h3 className="font-bold text-slate-900 text-sm truncate min-w-0" style={{ fontFamily: "'Manrope', sans-serif" }}>
                        {c.id}
                      </h3>
                      <span className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide bg-emerald-50 text-emerald-700 whitespace-nowrap">
                        Credit
                      </span>
                    </div>
                    <p className="text-xs text-slate-600 truncate min-w-0 mt-0.5">{c.source}</p>
                    <div className="flex gap-3 mt-1 text-xs min-w-0">
                      <span className="text-slate-400 whitespace-nowrap flex-shrink-0">{formatDate(c.date)}</span>
                      {c.sub_category && (
                        <span className="text-slate-500 truncate min-w-0 flex-shrink">{c.sub_category}</span>
                      )}
                      <span className="text-slate-400 truncate min-w-0 flex-shrink">By: {c.submitted_by}</span>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0 pl-3 border-l border-slate-100 whitespace-nowrap">
                    <p className="text-sm font-extrabold text-emerald-700">₹{Number(c.amount).toLocaleString('en-IN')}</p>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0 ml-1">
                    <Button variant="outline" size="sm" className="rounded-lg h-7 w-7 p-0" onClick={() => setViewing(c)} data-testid={`credit-view-${c.id}`}>
                      <Eye className="w-3.5 h-3.5" />
                    </Button>
                    <Button variant="outline" size="sm" className="rounded-lg h-7 w-7 p-0 text-blue-600 hover:bg-blue-50" onClick={() => openEdit(c)} data-testid={`credit-edit-${c.id}`}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    {isSuperApprover && (
                      <Button variant="outline" size="sm" className="rounded-lg h-7 w-7 p-0 text-red-600 hover:bg-red-50" onClick={() => remove(c.id)} data-testid={`credit-delete-${c.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2">
            <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>Prev</Button>
            <span className="text-sm text-slate-600">Page {page} of {totalPages}</span>
            <Button variant="outline" size="sm" disabled={page === totalPages} onClick={() => setPage((p) => p + 1)}>Next</Button>
          </div>
        )}
      </div>

      {/* Add / Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={(o) => { if (!o) { setShowDialog(false); setEditing(null); } }}>
        <DialogContent className="max-w-md" data-testid="credit-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Credit' : 'Add Credit'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label>Amount (₹) *</Label>
              <Input
                type="number" step="0.01" min="0.01"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                required
                data-testid="credit-form-amount"
              />
            </div>
            <div>
              <Label>Date *</Label>
              <Input
                type="date"
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                required
                data-testid="credit-form-date"
              />
            </div>
            <div>
              <Label>Source / Received From *</Label>
              <Input
                value={form.source}
                onChange={(e) => setForm({ ...form, source: e.target.value })}
                placeholder="e.g. Vendor refund, Insurance claim, Scrap buyer"
                required
                data-testid="credit-form-source"
              />
            </div>
            <div>
              <Label>Sub-category (optional)</Label>
              <Input
                value={form.sub_category}
                onChange={(e) => setForm({ ...form, sub_category: e.target.value })}
                placeholder="e.g. Refund, Rebate, Insurance, Scrap"
                data-testid="credit-form-subcategory"
              />
            </div>
            <div>
              <Label>Notes (optional)</Label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={2}
                data-testid="credit-form-notes"
              />
            </div>
            <div>
              <Label>Attachments (optional)</Label>
              <div className="flex items-center gap-2">
                <input
                  type="file"
                  multiple
                  onChange={(e) => setFiles(Array.from(e.target.files))}
                  className="text-xs"
                  data-testid="credit-form-files"
                />
                {files.length > 0 && <span className="text-xs text-slate-500">{files.length} selected</span>}
              </div>
              {editing?.attachments?.length > 0 && (
                <p className="text-[10px] text-slate-400 mt-1">{editing.attachments.length} file(s) already attached; new files will be appended.</p>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
              <Button type="submit" disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="credit-form-submit">
                {saving ? 'Saving…' : editing ? 'Update' : 'Add Credit'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={!!viewing} onOpenChange={(o) => { if (!o) setViewing(null); }}>
        <DialogContent className="max-w-md" data-testid="credit-view-dialog">
          <DialogHeader>
            <DialogTitle>{viewing?.id}</DialogTitle>
          </DialogHeader>
          {viewing && (
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Amount:</span><span className="font-semibold text-emerald-700">₹{Number(viewing.amount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Date:</span><span>{formatDate(viewing.date)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Source:</span><span>{viewing.source}</span></div>
              {viewing.sub_category && <div className="flex justify-between"><span className="text-slate-500">Sub-category:</span><span>{viewing.sub_category}</span></div>}
              {viewing.notes && (
                <div><div className="text-slate-500 mb-1">Notes:</div><div className="bg-slate-50 rounded p-2 text-slate-700">{viewing.notes}</div></div>
              )}
              <div className="flex justify-between"><span className="text-slate-500">Submitted by:</span><span>{viewing.submitted_by}</span></div>
              {viewing.attachments?.length > 0 && (
                <div>
                  <div className="text-slate-500 mb-1">Attachments:</div>
                  <div className="space-y-1">
                    {viewing.attachments.map((a, i) => (
                      <a
                        key={i}
                        href={`${API}/credits/${viewing.id}/file/${i}`}
                        target="_blank"
                        rel="noreferrer"
                        className="flex items-center gap-1 text-xs text-blue-600 hover:underline"
                        onClick={async (e) => {
                          e.preventDefault();
                          try {
                            const res = await axios.get(`${API}/credits/${viewing.id}/file/${i}`, {
                              headers: authHeader, responseType: 'blob',
                            });
                            const url = URL.createObjectURL(res.data);
                            window.open(url, '_blank');
                          } catch { toast.error('Failed to open file'); }
                        }}
                      >
                        <Upload className="w-3 h-3" /> {a.filename || `File ${i + 1}`}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
